import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router';
import DropDownCell from 'components/LineItemTable/dropDownCell';
import DocumentLink from 'components/LineItemTable/documentLink';
import InputCell from 'components/LineItemTable/inputCell';
import ItemEvents from 'components/LineItemTable/itemEvents';
import { EditableTableRow, EditableTableCell } from 'components/EditableTable';
import documentType from 'types/documentUpload';
import { formatDollars } from '../../../../../../../../utils/lineItemUtils';
import Editable from 'mixins/editable';
import { getSimpleLocalDate } from '../../../../../../../../utils/dateUtils';
import getCanUseDocumentOptions from '../../../../../../../../utils/documents';
import { urls, linkTo } from 'helpers/url';
import LaborSheetType from 'types/laborSheet';
import MediaQuery from 'react-responsive';
import { can } from '../../../../../../../../services';
import { NON_LHI_SOURCES, CUT_POINT } from 'config/constants';
import S from './laborTables.scss';

const STATUSES = ['Pending Review', 'Ready for Accounting', 'Approved by Contractor', 'Recorded', 'Not Payable'];
const RESTRICTED_STATUSES = ['Pending Review', 'Ready for Accounting', 'Approved by Contractor', 'Not Payable'];
const SIGNED_DOCUMENT_TYPES = ['Signed Documents'];
const READY_FOR_ACCOUNTING = 'Ready for Accounting';

class LaborSheetRow extends Editable(React.Component) {
  state = { originalStatus: this.props.lineItem.status, ...this.props.lineItem };

  handleChange = attr => (val) => {
    const value = this.sanatize(attr, val);
    this.setState({ [attr]: value });
  }

  handleReferenceChange = this.handleChange('quickbooks_reference');
  handleSignedDocumentChange = this.handleChange('signed_document_id');
  handleStatusChange = this.handleChange('status');

  handleSave = () => {
    this.props.onSave(this.state);
  }

  formatOptions = options => options.map(option => (
    { value: option, label: option }
  ));

  // Check if phase is qualified for LRRP (would normally require LRRP)
  isQualifiedForLrrp = () => {
    const { phase } = this.props;
    if (!phase) return false;
    const { lead_practices_required, year_built, source, workroom } = phase;
    if (!lead_practices_required) return false;
    if (year_built && year_built > 1977) return false;
    const noLrrpVendorsExceptIfi = NON_LHI_SOURCES.filter(v => v !== 'IFI');
    if (noLrrpVendorsExceptIfi.includes(source)) return false;
    // Use naming convention to match backend PFT workrooms (all start with 'PFT-')
    // This avoids hardcoding the list and matches existing client code patterns
    if (workroom && workroom.name && workroom.name.startsWith('PFT-')) return false;
    return true;
  }

  // Check if LRRP paperwork is completed
  isLrrpCompleted = () => {
    const { phase, documents } = this.props;
    if (!phase) return false;

    // Check for completed LRRP task when applicable
    // Prefer phase_tasks (full task history) over current_tasks (only tasks for current status)
    // This ensures we detect completed tasks even after phase moves to a different status
    // Only Installation and Workorder phases have LRRP tasks (matches backend logic)
    let lrrpTaskSlug = null;
    if (phase.type === 'Installation') {
      lrrpTaskSlug = 'sign_lrrp';
    } else if (phase.type === 'Workorder') {
      lrrpTaskSlug = 'lead_assessment';
    }

    if (lrrpTaskSlug) {
      const allTasks = (phase.phase_tasks && Array.isArray(phase.phase_tasks))
        ? phase.phase_tasks
        : (phase.current_tasks || []);
      const lrrpTask = allTasks.find(
        task => task && task.slug === lrrpTaskSlug && task.status === 'complete',
      );
      if (lrrpTask) return true;
    }

    // Check for LRRP document
    const lrrpDocument = documents && documents.find(
      doc => doc.document_type === 'LRRP Checklists' && !doc.not_use,
    );
    if (lrrpDocument) return true;

    return false;
  }

  // Check if phase has build date
  hasBuildDate = () => {
    const { phase } = this.props;
    return phase && phase.year_built != null;
  }

  // Check if "Ready for Accounting" should be blocked
  shouldBlockReadyForAccounting = () => {
    if (!this.isQualifiedForLrrp()) return false;
    return !(this.hasBuildDate() && this.isLrrpCompleted());
  }

  determineStatusChoices = (status) => {
    let statuses;
    if (['admin', 'accounting'].includes(this.props.role)) {
      statuses = STATUSES;
    } else if (RESTRICTED_STATUSES.includes(status)) {
      statuses = RESTRICTED_STATUSES;
    } else {
      statuses = RESTRICTED_STATUSES.concat([status]);
    }

    // Filter out "Ready for Accounting" if LRRP requirements aren't met,
    // but keep it if it's already the current status so the dropdown
    // can still display the existing value.
    if (this.shouldBlockReadyForAccounting() && status !== READY_FOR_ACCOUNTING) {
      statuses = statuses.filter(s => s !== READY_FOR_ACCOUNTING);
    }

    return statuses;
  }

  /*
   * This is using the same logic used to generate the Approve/Approved
   * button on a given labor table to maintain consistency.
   */
  determineAppproved = (lineItem) => {
    const { status, signed_document_id } = lineItem;
    if ((status === 'Approved by Contractor' && signed_document_id)
      || (
        status !== 'Approved by Contractor'
        && status !== 'Not Payable'
        && status !== 'Pending Review'
      )
    ) {
      return true;
    }
    return false;
  }

  determineBlockedByApproved = (approved, role) => {
    if (approved && !can(role, 'manage', 'approved_labor')) {
      return true;
    }
    return false;
  }

  /*
   * As long as the given role isn't readOnly in the first place and
   * the original status (before changing and saving in edit mode),
   * we can edit the line item. This allows certain roles to change
   * to a 'forbidden status' and save the change, waiting until
   * after they make their allowed changes to block them from editing.
   */
  determineCanEdit = (readOnly, blockedByApproved) => {
    const tempLineItem = { status: this.state.originalStatus, signed_document_id: null };
    if (!readOnly &&
      (!blockedByApproved || (blockedByApproved && !this.determineAppproved(tempLineItem)))
    ) {
      return true;
    }
    return false;
  }

  renderAppointmentLink = () => {
    const { appointment } = this.state;
    const appointmentUrlObject = { ...appointment };
    appointmentUrlObject.phase = { id: this.props.phaseId };
    return (
      <Link to={urls.appointment(appointmentUrlObject)}>
        {getSimpleLocalDate(appointment.end_date)}
      </Link>
    );
  }

  renderTableDesktop = () => {
    const lineItem = this.state;
    const { documents, index, readOnly, role, canViewTotal, toggleLineItem } = this.props;
    const documentOptions = getCanUseDocumentOptions(documents, SIGNED_DOCUMENT_TYPES);
    const total = formatDollars(lineItem.total);
    const appointmentLink = this.renderAppointmentLink();
    const assigneeLink = <Link to={linkTo(lineItem.assignee)} target="_blank">{lineItem.assignee.name}</Link>;
    const approved = this.determineAppproved(lineItem);
    const blockedByApproved = this.determineBlockedByApproved(approved, role);
    const canEdit = this.determineCanEdit(readOnly, blockedByApproved);
    const rowProps =
      { isNew: lineItem.id < 0, onSave: this.handleSave, readOnly: !canEdit };

    return (
      <EditableTableRow
        key={lineItem.id}
        id={lineItem.id}
        deleteDialog
        {...rowProps}
        toggleLineItem={toggleLineItem}
        expandableRow
      >
        <ItemEvents
          display={!this.context.isEditable}
          label=""
          itemType="laborSheets"
          lineItem={lineItem}
          dataKeys={['assignee.name', 'signed_document.document_name', 'status', 'total', 'quickbooks_reference']}
        />
        <EditableTableCell value={index} label="Id">
          {index}
        </EditableTableCell>
        <EditableTableCell value={assigneeLink} label="Contractor">
          {assigneeLink}
        </EditableTableCell>
        <EditableTableCell value={appointmentLink} label="Appointment End">
          {appointmentLink}
        </EditableTableCell>
        <DropDownCell
          attr="signed_document"
          isIdAttr
          label="Signed Document"
          lineItem={lineItem}
          options={documentOptions}
          onChange={this.handleSignedDocumentChange}
          clearable
        >
          <DocumentLink documents={documents} id={lineItem.signed_document_id} />
        </DropDownCell>
        <DropDownCell
          attr="status"
          label="Payment Status"
          lineItem={lineItem}
          options={this.formatOptions(this.determineStatusChoices(lineItem.status))}
          onChange={this.handleStatusChange}
        />
        {canViewTotal && <EditableTableCell value={total} label="Total">
          {total}
        </EditableTableCell>}
        <InputCell attr="quickbooks_reference" label="QB Reference" lineItem={lineItem} onChange={this.handleReferenceChange} />
      </EditableTableRow>
    );
  }

  renderTableMobile = () => {
    const lineItem = this.state;
    const { documents, index, readOnly, role, canViewTotal, toggleLineItem } = this.props;
    const documentOptions = getCanUseDocumentOptions(documents, SIGNED_DOCUMENT_TYPES);
    const total = formatDollars(lineItem.total);
    const appointmentLink = this.renderAppointmentLink();
    const assigneeLink = <Link to={linkTo(lineItem.assignee)} target="_blank">{lineItem.assignee.name}</Link>;
    const approved = this.determineAppproved(lineItem);
    const blockedByApproved = this.determineBlockedByApproved(approved, role);
    const canEdit = this.determineCanEdit(readOnly, blockedByApproved);
    const rowProps =
      { isNew: lineItem.id < 0, onSave: this.handleSave, readOnly: !canEdit };

    return (
      <EditableTableRow
        key={lineItem.id}
        id={lineItem.id}
        deleteDialog
        toggleLineItem={toggleLineItem}
        {...rowProps}
      >
        <EditableTableCell value={index} label="Id">
          {index}
        </EditableTableCell>
        <EditableTableCell value={assigneeLink} label="Contractor">
          {assigneeLink}
        </EditableTableCell>
        <EditableTableCell value={appointmentLink} label="Appointment End">
          {appointmentLink}
        </EditableTableCell>
        <DropDownCell
          attr="signed_document"
          isIdAttr
          label="Signed Document"
          lineItem={lineItem}
          options={documentOptions}
          onChange={this.handleSignedDocumentChange}
          clearable
        >
          <DocumentLink documents={documents} id={lineItem.signed_document_id} />
        </DropDownCell>
        <DropDownCell
          attr="status"
          label="Payment Status"
          lineItem={lineItem}
          options={this.formatOptions(this.determineStatusChoices(lineItem.status))}
          onChange={this.handleStatusChange}
        />
        {canViewTotal && <EditableTableCell value={total} label="Total">
          {total}
        </EditableTableCell>}
        <InputCell attr="quickbooks_reference" label="QB Reference" lineItem={lineItem} onChange={this.handleReferenceChange} />
        <td>
          <div>
            <table className={S.inlineTableItem}>
              <tbody className={S.inlineTableItem}>
                <tr className={S.iconWrapper}>
                  <ItemEvents
                    display={!this.context.isEditable}
                    label=""
                    itemType="laborSheets"
                    lineItem={lineItem}
                    dataKeys={['assignee.name', 'signed_document.document_name', 'status', 'total', 'quickbooks_reference']}
                  />
                </tr>
              </tbody>
            </table>
          </div>
        </td>
      </EditableTableRow>
    );
  }

  render() {
    return (
      <MediaQuery minWidth={CUT_POINT}>
        {(matches) => {
          if (matches) {
            return this.renderTableDesktop();
          }
          return this.renderTableMobile();
        }}
      </MediaQuery>
    );
  }
}

LaborSheetRow.contextTypes = {
  isEditable: PropTypes.bool,
};

LaborSheetRow.propTypes = {
  role: PropTypes.string,
  lineItem: LaborSheetType.isRequired,
  onSave: PropTypes.func.isRequired,
  documents: PropTypes.arrayOf(documentType).isRequired,
  index: PropTypes.number,
  phaseId: PropTypes.string,
  readOnly: PropTypes.bool,
  canViewTotal: PropTypes.bool,
  phase: PropTypes.object,
};

export default LaborSheetRow;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/phaseAccounting/components/laborSheetRow.jsx